import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionHeader } from '@src/components/ServicePopover/SectionHeader'
import { BottomSheet } from '@src/components/BottomSheet/BottomSheet'
import {
  MobileNavigationPopoverContentWrapper,
  MobileNavigationPopoverContent,
  MobileNavigationPopoverFooter,
  MobileNavigationPopoverFooterContent,
  MobileNavigationResetLink,
  DoneButton,
} from '@src/components/BottomSheet/BottomSheet.styles'
import { useLazyQuery } from '@apollo/client'
import { useMarketplace } from '../../hooks/useMarketplace'
import { SearchAddressDocument } from './queries/__generated__/SearchAddress.graphql-interface'
import { debounce } from 'lodash'
import { PlaceSearchLocation } from '../../hooks/useFulfilmentFilter/validation'
import { useBottomSheetStack } from '../../hooks/useBottomSheetStack/useBottomSheetStack'
import { LocationSearch } from '../LocationSearch/LocationSearch'

type PlaceSearchLocationWithoutType = Omit<PlaceSearchLocation, 'type'>
export const PlaceSearchPopover: React.FC<{
  onChange?: (place: PlaceSearchLocationWithoutType) => void
  onDone: (placeId: PlaceSearchLocationWithoutType) => void
  isPopover: boolean
}> = ({ onChange, onDone, isPopover }) => {
  const { placeSearchBottomSheetController } = useBottomSheetStack()
  const { t } = useTranslation('serviceNavigation')
  const [searchTerm, setSearchTerm] = useState('')
  const { id } = useMarketplace()
  const [loading, setLoading] = useState(false)
  const [newPlace, setNewPlace] =
    useState<PlaceSearchLocationWithoutType | null>(null)

  const [triggerQuery, { data: _data, previousData }] = useLazyQuery(
    SearchAddressDocument,
    { onCompleted: () => setLoading(false) }
  )

  const data = _data || previousData

  const debouncedSearch = useMemo(
    () =>
      debounce((term: string) => {
        if (term.length >= 5) {
          void triggerQuery({
            variables: {
              searchTerm: term,
              marketplaceId: id,
            },
          })
        }
      }, 500),
    [id, triggerQuery]
  )

  useEffect(() => {
    if (searchTerm.length >= 5) {
      setLoading(true)
      debouncedSearch(searchTerm)
    }
  }, [searchTerm, debouncedSearch])

  return (
    <BottomSheet
      bottomSheetController={placeSearchBottomSheetController}
      maxWidth={584}
      showBackdrop={!isPopover}
    >
      <MobileNavigationPopoverContentWrapper>
        <MobileNavigationPopoverContent>
          <SectionHeader
            header={t('location_header')}
            subheader={t('place_search_option_subtitle')}
          />
          <br />
          <LocationSearch
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            loading={loading}
            data={data?.searchAddress}
            selectedPlace={newPlace}
            onChange={place => {
              setNewPlace(place)
              onChange?.(place)
            }}
            searchLabel={t('search_label')}
            searchPlaceholder={t('search_placeholder')}
          />
        </MobileNavigationPopoverContent>
      </MobileNavigationPopoverContentWrapper>
      <MobileNavigationPopoverFooter>
        <MobileNavigationPopoverFooterContent>
          <MobileNavigationResetLink
            onClick={placeSearchBottomSheetController.close}
          >
            {t('cancel')}
          </MobileNavigationResetLink>

          <DoneButton
            content={t('done')}
            width="180px"
            onClick={e => {
              e?.stopPropagation()
              if (newPlace) {
                onDone(newPlace)
                placeSearchBottomSheetController.close()
              }
            }}
            type={'button'}
            disabled={!newPlace}
          />
        </MobileNavigationPopoverFooterContent>
      </MobileNavigationPopoverFooter>
    </BottomSheet>
  )
}
